// MuroCursos.js — Banner de fondo con las portadas reales de los cursos del
// catálogo: tres filas inclinadas que se deslizan despacio, un velo del color de
// fondo para que el texto se lea y un resplandor del acento. Lo usan las páginas
// «Nosotros» y los documentos legales. Con `imagen` pinta esa foto en su lugar.
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { BACKEND_BASE } from '../../../services/api';

const urlImg = (v) => (!v ? '' : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));
const deriva = keyframes`from { transform: translate3d(0, 0, 0); } to { transform: translate3d(-50%, 0, 0); }`;

const Capa = styled.div`
  position: absolute; inset: 0; z-index: -1; overflow: hidden; pointer-events: none;
`;
const Filas = styled.div`
  position: absolute; inset: -12% -12% auto; transform: rotate(-7deg);
  display: flex; flex-direction: column; gap: 14px; opacity: ${p => p.$opacidad};
  .fila { display: flex; gap: 14px; width: max-content; animation: ${deriva} 75s linear infinite; }
  .fila:nth-child(2) { animation-duration: 95s; animation-direction: reverse; }
  .fila:nth-child(3) { animation-duration: 85s; }
  img { width: clamp(230px, 23vw, 360px); aspect-ratio: 16 / 9; object-fit: cover; border-radius: 10px; display: block;
    box-shadow: 0 18px 40px -20px rgba(0,0,0,.9); }
  @media (prefers-reduced-motion: reduce) { .fila { animation: none; } }
`;
const Foto = styled.img`position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: ${p => p.$opacidad};`;
const Velo = styled.div`
  position: absolute; inset: 0;
  background:
    radial-gradient(60% 70% at 85% 10%, color-mix(in srgb, ${p => p.$acento} 35%, transparent), transparent 70%),
    linear-gradient(to top, ${p => p.$fondo} 4%, color-mix(in srgb, ${p => p.$fondo} 72%, transparent) 45%, color-mix(in srgb, ${p => p.$fondo} 25%, transparent) 100%),
    linear-gradient(to right, ${p => p.$fondo} 0%, color-mix(in srgb, ${p => p.$fondo} 55%, transparent) 45%, transparent 80%);
`;

const MuroCursos = ({ proyecto, fondo = '#141414', acento = '#E50914', imagen, opacidad = 0.8 }) => {
  const portadas = useMemo(() => {
    const cat = (proyecto?.config_json?.landing_secciones || []).find(s => s.tipo === 'catalogo_streaming')?.config || {};
    const docs = proyecto?.documentos || [];
    const imgs = (cat.items || [])
      .map(it => urlImg(it.imagen_cartelera || it.imagen || docs.find(d => String(d.id) === String(it.documento_id))?.imagen))
      .filter(Boolean);
    return [...new Set(imgs)];
  }, [proyecto]);

  if (!imagen && !portadas.length) return null;
  return (
    <Capa aria-hidden="true">
      {imagen
        ? <Foto src={urlImg(imagen)} alt="" $opacidad={opacidad} />
        : (
          <Filas $opacidad={opacidad}>
            {[0, 1, 2].map(f => {
              // Cada fila empieza en otra portada y se repite para cubrir el ancho al deslizarse.
              const base = [...portadas.slice(f % portadas.length), ...portadas.slice(0, f % portadas.length)];
              let fila = []; while (fila.length < 16) fila = fila.concat(base);
              return <div className="fila" key={f}>{fila.slice(0, 16).map((src, i) => <img key={i} src={src} alt="" loading="lazy" />)}</div>;
            })}
          </Filas>
        )}
      <Velo $fondo={fondo} $acento={acento} />
    </Capa>
  );
};

export default MuroCursos;
